import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Typography,
  Box,
  Tabs,
  Tab,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ArrowBack,
  LocationOn,
  Category,
  PinDrop,
  Block,
} from "@mui/icons-material";
import ToggleSwitch from "../backend/ToggleSwitch";
import { useDropzone } from "react-dropzone";
import WayfinderButtonSettings from "./WayfinderButtonSettings";
import { MultiSelectDropdown } from "./MultiSelectDopdown";
import { getLanguageText } from "../../utils/multilanguageUtils";
import StepByStepDirectionsConfig from "./StepByStepDirectionsConfig";

const WayfinderEditor = ({
  userId,
  type,
  item,
  setItem,
  loading,
  buttonColor,
  handleUploadPng,
  handleUploadPromoVideo,
  handleSave,
  handleDelete,
  resolvedButtonSettings,
  btnPng,
  setBtnPng,
  handleUploadClickSound,
  clickSoundFile,
  setClickSoundFile,
  promoVideoFile,
  setPromoVideoFile,
  subcategories = [],
  locations = [],
  onSubcategoriesChange,
  onLocationsChange,
  selectedExistingSubCategories,
  setSelectedExistingSubCategories,
  currentLanguage = 'en', // Add currentLanguage prop with default
  stepByStepConfig,
  setStepByStepConfig,
  saveStepByStepConfig,
  parentOptions = [],
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  // Local navigation type to keep radio selection stable during re-renders
  const [navigationType, setNavigationType] = useState(
    item?.navigation?.type || "location",
  );

  console.log("subcategories", subcategories);
  console.log("WayfinderEditor currentLanguage:", currentLanguage);

  // Process locations for MultiSelectDropdown to handle multilanguage names
  const processedLocations = React.useMemo(() => {
    return (locations || []).map(location => ({
      ...location,
      name: getLanguageText(location.name, currentLanguage) || getLanguageText(location.buttonText, currentLanguage) || 'Untitled Location',
      label: getLanguageText(location.name, currentLanguage) || getLanguageText(location.buttonText, currentLanguage) || 'Untitled Location'
    }));
  }, [locations, currentLanguage]);

  // Process subcategories for MultiSelectDropdown to handle multilanguage names
  const processedSubcategories = React.useMemo(() => {
    return (subcategories || []).map(subcategory => ({
      ...subcategory,
      name: getLanguageText(subcategory.name, currentLanguage) || getLanguageText(subcategory.buttonText, currentLanguage) || 'Untitled Subcategory',
      label: getLanguageText(subcategory.name, currentLanguage) || getLanguageText(subcategory.buttonText, currentLanguage) || 'Untitled Subcategory'
    }));
  }, [subcategories, currentLanguage]);

  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);

  useEffect(() => {
    if (
      item?.navigation?.locations &&
      Array.isArray(item.navigation.locations)
    ) {
      const locationIds = item.navigation.locations.map((loc) => {
        // Handle both ObjectId strings and populated objects
        if (typeof loc === "string") {
          return String(loc).trim();
        } else if (loc && loc._id) {
          return String(loc._id).trim();
        }
        return String(loc).trim();
      });
      setSelectedLocations(locationIds);
      console.log("Synced selected locations:", locationIds);
    } else {
      setSelectedLocations([]);
    }
  }, [item?.navigation?.locations]);

  useEffect(() => {
    if (
      item?.navigation?.subcategories &&
      Array.isArray(item.navigation.subcategories)
    ) {
      const subcategoryIds = item.navigation.subcategories.map((sub) => {
        // Handle both ObjectId strings and populated objects
        if (typeof sub === "string") {
          return String(sub).trim();
        } else if (sub && sub._id) {
          return String(sub._id).trim();
        }
        return String(sub).trim();
      });
      setSelectedSubCategories(subcategoryIds);
      if (setSelectedExistingSubCategories) {
        setSelectedExistingSubCategories(subcategoryIds);
      }
      console.log("Synced selected subcategories:", subcategoryIds);
    } else {
      setSelectedSubCategories([]);
      if (setSelectedExistingSubCategories) {
        setSelectedExistingSubCategories([]);
      }
    }
  }, [item?.navigation?.subcategories, setSelectedExistingSubCategories]);

  useEffect(() => {
    setNavigationType(item?.navigation?.type || "subcategory");
  }, [item?.navigation?.type]);

  const onNavigationChange = (event) => {
    setNavigationType(event.target.value);
    handleNavigationTypeChange(event);
  };

  // Navigation radio options (vary by editor `type`)
  const navigationOptions = React.useMemo(() => {
    if (type === "category") {
      return [
        { value: "location", label: "Locations", icon: <PinDrop /> },
      ];
    }
    // For subcategory editors show only directLocation and location (backend SubCategory uses camelCase)
    if (type === "subcategory") {
      return [
        
        { value: "location", label: "Locations", icon: <PinDrop /> },
      ];
    }
    return [{ value: "none", label: "None", icon: <Block /> }];
  }, [type]);

 

 

  const isDirectType = (t) => t === "direct_location" || t === "directLocation";
  const isSubCategoryType = (t) => t === "subcategory" || t === "subCategory";
  const isLocationType = (t) => t === "location";

  const handleNavigationTypeChange = (event) => {
    const newType = event.target.value;

    setNavigationType(newType);

    setItem((prev) => ({
      ...prev,
      navigation: {
        ...prev.navigation,
        type: newType,
        // ❌ DO NOT clear existing selections
        subcategories: prev.navigation?.subcategories || [],
        locations: prev.navigation?.locations || [],
        directLocation: prev.navigation?.directLocation || null,
      },
    }));
  };

  const handleSubcategoriesChange = (selectedSubcategoryIds) => {
    console.log(
      "handleSubcategoriesChange called with:",
      selectedSubcategoryIds,
    );

    setItem((prev) => ({
      ...prev,
      navigation: {
        ...prev.navigation,
        type: "subcategory", // Make sure type is set correctly
        subcategories: selectedSubcategoryIds,
      },
      // Keep top-level subCategories in sync for backend schema
      subCategories: selectedSubcategoryIds,
    }));

    // Update local state
    if (setSelectedExistingSubCategories) {
      setSelectedExistingSubCategories(selectedSubcategoryIds);
    }

    // Also call parent handler if provided
    if (onSubcategoriesChange) {
      onSubcategoriesChange(selectedSubcategoryIds);
    }
  };

  // Update the handleLocationsChange function
  const handleLocationsChange = (selectedLocationIds) => {
    console.log("handleLocationsChange called with:", selectedLocationIds);

    // Normalize the IDs to strings
    const normalizedIds = selectedLocationIds.map((id) => String(id).trim());
    setSelectedLocations(normalizedIds);

    setItem((prev) => ({
      ...prev,
      navigation: {
        ...prev.navigation,
        type: "location",
        locations: normalizedIds,
      },
      locations: normalizedIds,
    }));

    if (onLocationsChange) {
      onLocationsChange(normalizedIds);
    }
  };

  // Update the handleDirectLocationChange function
  const handleDirectLocationChange = (locationId) => {
    const directType =
      type === "category" ? "direct_location" : "directLocation";
    console.log("handleDirectLocationChange called with:", locationId);

    setItem((prev) => ({
      ...prev,
      navigation: {
        ...prev.navigation,
        type: directType,
        // store single directLocation id and also keep locations array for compatibility
        directLocation: locationId || null,
        locations: locationId ? [locationId] : [],
        subcategories: [], // Clear subcategories when switching to direct location
      },
      // Top-level flag kept for backward compatibility
      directLocation: !!locationId,
      locations: locationId ? [locationId] : [],
      subCategories: [], // Clear subcategories
    }));

    // Clear selected subcategories
    if (setSelectedExistingSubCategories) {
      setSelectedExistingSubCategories([]);
    }
  };

 

 

  if (loading && !getLanguageText(item.name, currentLanguage)) {
    return (
      <div className="main-container">
        <div className="pd-ltr-20 xs-pd-20-10">
          <div className="min-height-200px">
            <div className="page-header">
              <Typography variant="h4">
                Loading {type === "category" ? "Category" : "SubCategory"}{" "}
                Settings...
              </Typography>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px dashboard-container">
          {/* Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-md-6 col-sm-12">
                <div className="title">
                  <Typography variant="h4">
                    Wayfinder {">>"}{" "}
                    <span style={{ color: buttonColor }}>
                      {type === "category" ? "Category" : "SubCategory"}
                    </span>
                    {">>"}{" "}
                    <span style={{ color: "blue" }}>
                      {getLanguageText(item.buttonText, currentLanguage)?.replace(/<br\s*\/?>/gi, "") ||
                        (type === "category" ? "Category" : "SubCategory")}
                    </span>
                  </Typography>
                </div>
              </div>
              <div className="col-md-6 col-sm-12" style={{ width: "100%" }}>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  style={{ float: "right", backgroundColor: buttonColor }}
                  className="btn btn-success"
                >
                  <ArrowBack style={{ marginRight: "5px" }} />
                  Go Back
                </button>
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": { minWidth: 100 },
              "& .Mui-selected": {
                fontWeight: "bold",
                color: buttonColor,
                borderBottom: `2px solid ${buttonColor}`,
              },
            }}
          >
             
              <Tab
                key={"Location Manager"}
                label={"Location Manager"}
                sx={{
                  "&:hover": {
                    backgroundColor: `${buttonColor}20`,
                    color: buttonColor,
                  },
                }}
              />
              <Tab
                key={"Step-by-Step Directions"}
                label={"Step-by-Step Directions"}
                sx={{
                  "&:hover": {
                    backgroundColor: `${buttonColor}20`,
                    color: buttonColor,
                  },
                }}
              />
            
          </Tabs>

          <Box sx={{ p: 4 }}>
            {/* {activeTab === 0 && (
              <WayfinderButtonSettings
                handleDelete={handleDelete}
                resolvedButtonSettings={resolvedButtonSettings}
                btnPng={btnPng}
                setBtnPng={setBtnPng}
                handleUploadClickSound={handleUploadClickSound}
                clickSoundFile={clickSoundFile}
                setClickSoundFile={setClickSoundFile}
                handleUploadPng={handleUploadPng}
                uploadingStates={uploadingStates}
                item={item}
                setItem={setItem}
                handleSave={handleSave}
                buttonColor={buttonColor}
              />
            )} */}

            
            {activeTab === 0 && type === "subcategory" && (
              <div
                className="menu-card2"
                style={{ padding: "20px", marginTop: "20px" }}
              >
                <Box sx={{ width: "100%", mx: "auto" }}>
                  <CardContent>
                    {/* Navigation Type Selection */}
                    <FormControl component="fieldset" sx={{ mb: 4 }}>
                      <FormLabel
                        component="legend"
                        sx={{ mb: 2, fontWeight: "bold" }}
                      >
                        Select Navigation Type
                      </FormLabel>
                      <RadioGroup
                        value={navigationType}
                        onChange={onNavigationChange}
                        sx={{ gap: 2, display: "flex", flexDirection: "row" }}
                      >
                        {navigationOptions.map((option) => (
                          <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={
                              <Radio
                                sx={{
                                  color: buttonColor,
                                  "&.Mui-checked": { color: buttonColor },
                                }}
                              />
                            }
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {option.icon}
                                <Typography>{option.label}</Typography>
                              </Box>
                            }
                            sx={{
                              border: `1px solid ${navigationType === option.value ? buttonColor : "#e0e0e0"}`,
                              borderRadius: 1,
                              padding: 2,
                              backgroundColor:
                                navigationType === option.value
                                  ? `${buttonColor}10`
                                  : "transparent",
                              "&:hover": {
                                backgroundColor: `${buttonColor}15`,
                              },
                            }}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>

                    {/* {isDirectType(navigationType) && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ fontWeight: "bold" }}
                        >
                          Select Direct Location
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={item.navigation?.locations?.[0] || ""}
                            onChange={(e) =>
                              handleDirectLocationChange(e.target.value)
                            }
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {locations.map((loc) => (
                              <MenuItem key={loc._id} value={loc._id}>
                                {getLanguageText(loc.name, currentLanguage) ||
                                  getLanguageText(loc.buttonText, currentLanguage) ||
                                  "Untitled Location"}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: "block" }}
                        >
                          This location will be opened directly when the user
                          taps this{" "}
                          {type === "category" ? "category" : "subcategory"}.
                        </Typography>
                      </Box>
                    )}

                    {isSubCategoryType(navigationType) && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ fontWeight: "bold" }}
                        >
                          Select Subcategories
                        </Typography>
                        <MultiSelectDropdown
                          options={processedSubcategories || []}
                          selectedValues={(
                            item.navigation?.subcategories ||
                            selectedExistingSubCategories ||
                            []
                          ).map((id) => String(id).trim())}
                          onChange={(val) => handleSubcategoriesChange(val)}
                          placeholder="Search and select subcategories..."
                          label="Available Subcategories"
                          isSearchable={true}
                          isClearable={true}
                          currentLanguage={currentLanguage}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: "block" }}
                        >
                          Selected subcategories will appear when users click
                          this category.
                        </Typography>
                      </Box>
                    )} */}

                    {/* Locations Selection (multi-select) */}
                    {isLocationType(navigationType) && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ fontWeight: "bold" }}
                        >
                          Select Locations
                        </Typography>
                        <MultiSelectDropdown
                          options={processedLocations || []}
                          selectedValues={
                            item?.navigation?.locations ||
                            selectedLocations?.map((id) => String(id).trim())
                          }
                          onChange={handleLocationsChange}
                          placeholder="Search and select locations..."
                          label="Available Locations"
                          isSearchable={true}
                          isClearable={true}
                          currentLanguage={currentLanguage}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: "block" }}
                        >
                          Selected locations will appear when users click this
                          category.
                        </Typography>

                        {/* Order Locations Button */}
                        {/* {selectedLocations && selectedLocations.length > 0 && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              backgroundColor: "#f8fafc",
                              borderRadius: "10px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#334155",
                                    mb: 0.5,
                                  }}
                                >
                                  Order Locations
                                </Typography>
                                <Typography
                                  sx={{ fontSize: "12px", color: "#64748b" }}
                                >
                                  Set the display order for selected locations
                                </Typography>
                              </Box>
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/parent-location-order/${item.userId}/${item._id}/${type}`,
                                  )
                                }
                                style={{
                                  padding: "8px 16px",
                                  backgroundColor: buttonColor || "#6366f1",
                                  color: "#ffffff",
                                  border: "none",
                                  borderRadius: "8px",
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <line x1="8" y1="6" x2="21" y2="6" />
                                  <line x1="8" y1="12" x2="21" y2="12" />
                                  <line x1="8" y1="18" x2="21" y2="18" />
                                  <line x1="3" y1="6" x2="3.01" y2="6" />
                                  <line x1="3" y1="12" x2="3.01" y2="12" />
                                  <line x1="3" y1="18" x2="3.01" y2="18" />
                                </svg>
                                Order Locations
                              </button>
                            </Box>
                          </Box>
                        )} */}
                      </Box>
                    )}

                    {/* Direct location info */}
                    {isDirectType(navigationType) && (
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: `${buttonColor}10`,
                          borderRadius: 1,
                          border: `1px solid ${buttonColor}30`,
                        }}
                      >
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>Direct Location Selected</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Additional settings for location management are
                          available in the "Location Settings" and "Avatar
                          Settings" tabs that have been added to the navigation.
                        </Typography>
                      </Box>
                    )}

                    <Box
                      sx={{
                        mt: 4,
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: buttonColor,
                          color: "white",
                          border: "none",
                          padding: "10px 20px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                        type="button"
                        onClick={handleSave}
                      >
                        Save Navigation Settings
                      </button>
                    </Box>
                  </CardContent>
                </Box>
              </div>
            )}

            {activeTab === 0 && type === "category" && (
              <div
                className="menu-card2"
                style={{ padding: "20px", marginTop: "20px" }}
              >
                <Box sx={{ width: "100%", mx: "auto" }}>
                  <CardContent>
                    {/* Navigation Type Selection */}
                    <FormControl component="fieldset" sx={{ mb: 4 }}>
                      <FormLabel
                        component="legend"
                        sx={{ mb: 2, fontWeight: "bold" }}
                      >
                        Select Navigation Type
                      </FormLabel>
                      <RadioGroup
                        value={navigationType}
                        onChange={onNavigationChange}
                        sx={{ gap: 2, display: "flex", flexDirection: "row" }}
                      >
                        {navigationOptions.map((option) => (
                          <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={
                              <Radio
                                sx={{
                                  color: buttonColor,
                                  "&.Mui-checked": { color: buttonColor },
                                }}
                              />
                            }
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {option.icon}
                                <Typography>{option.label}</Typography>
                              </Box>
                            }
                            sx={{
                              border: `1px solid ${navigationType === option.value ? buttonColor : "#e0e0e0"}`,
                              borderRadius: 1,
                              padding: 2,
                              backgroundColor:
                                navigationType === option.value
                                  ? `${buttonColor}10`
                                  : "transparent",
                              "&:hover": {
                                backgroundColor: `${buttonColor}15`,
                              },
                            }}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>

                    {/* Direct Location Selection (single location) */}
                    {isDirectType(item.navigation?.type) && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ fontWeight: "bold" }}
                        >
                          Select Direct Location
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={item.navigation?.directLocation || ""}
                            onChange={(e) =>
                              handleDirectLocationChange(e.target.value)
                            }
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {locations.map((loc) => (
                              <MenuItem key={loc._id} value={loc._id}>
                                {getLanguageText(loc.name, currentLanguage) ||
                                  getLanguageText(loc.buttonText, currentLanguage) ||
                                  "Untitled Location"}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: "block" }}
                        >
                          This location will be opened directly when the user
                          taps this{" "}
                          {type === "category" ? "category" : "subcategory"}.
                        </Typography>
                      </Box>
                    )}

                    {/* Subcategories Selection (multi-select) */}
                    {isSubCategoryType(navigationType) && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ fontWeight: "bold" }}
                        >
                          Select Subcategories
                        </Typography>
                        <MultiSelectDropdown
                          options={processedSubcategories || []}
                          selectedValues={(
                            selectedExistingSubCategories || []
                          ).map((id) => String(id).trim())}
                          onChange={(val) => handleSubcategoriesChange(val)}
                          placeholder="Search and select subcategories..."
                          label="Available Subcategories"
                          isSearchable={true}
                          isClearable={true}
                          currentLanguage={currentLanguage}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: "block" }}
                        >
                          Selected subcategories will appear when users click
                          this category.
                        </Typography>

                        {/* Order Subcategories Button */}
                        {/* {selectedExistingSubCategories &&
                          selectedExistingSubCategories.length > 0 && (
                            <Box
                              sx={{
                                mt: 2,
                                p: 2,
                                backgroundColor: "#f8fafc",
                                borderRadius: "10px",
                                border: "1px solid #e2e8f0",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#334155",
                                      mb: 0.5,
                                    }}
                                  >
                                    Order Subcategories
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: "12px", color: "#64748b" }}
                                  >
                                    Set the display order for selected
                                    subcategories
                                  </Typography>
                                </Box>
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/subcategory-order/${userId}/${item._id}`,
                                    )
                                  }
                                  style={{
                                    padding: "8px 16px",
                                    backgroundColor: buttonColor || "#6366f1",
                                    color: "#ffffff",
                                    border: "none",
                                    borderRadius: "8px",
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <line x1="8" y1="6" x2="21" y2="6" />
                                    <line x1="8" y1="12" x2="21" y2="12" />
                                    <line x1="8" y1="18" x2="21" y2="18" />
                                    <line x1="3" y1="6" x2="3.01" y2="6" />
                                    <line x1="3" y1="12" x2="3.01" y2="12" />
                                    <line x1="3" y1="18" x2="3.01" y2="18" />
                                  </svg>
                                  Order Subcategories
                                </button>
                              </Box>
                            </Box>
                          )} */}
                      </Box>
                    )}

                    {/* Locations Selection (multi-select) */}
                    {isLocationType(navigationType) && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ fontWeight: "bold" }}
                        >
                          Select Locations
                        </Typography>
                        <MultiSelectDropdown
                          options={processedLocations || []}
                          selectedValues={selectedLocations.map((id) =>
                            String(id).trim(),
                          )}
                          onChange={handleLocationsChange}
                          placeholder="Search and select locations..."
                          label="Available Locations"
                          isSearchable={true}
                          isClearable={true}
                          currentLanguage={currentLanguage}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: "block" }}
                        >
                          Selected locations will appear when users click this
                          category.
                        </Typography>

                        {/* Order Locations Button */}
                        {/* {selectedLocations && selectedLocations.length > 0 && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              backgroundColor: "#f8fafc",
                              borderRadius: "10px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#334155",
                                    mb: 0.5,
                                  }}
                                >
                                  Order Locations
                                </Typography>
                                <Typography
                                  sx={{ fontSize: "12px", color: "#64748b" }}
                                >
                                  Set the display order for selected locations
                                </Typography>
                              </Box>
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/parent-location-order/${item.userId}/${item._id}/${type}`,
                                  )
                                }
                                style={{
                                  padding: "8px 16px",
                                  backgroundColor: buttonColor || "#6366f1",
                                  color: "#ffffff",
                                  border: "none",
                                  borderRadius: "8px",
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <line x1="8" y1="6" x2="21" y2="6" />
                                  <line x1="8" y1="12" x2="21" y2="12" />
                                  <line x1="8" y1="18" x2="21" y2="18" />
                                  <line x1="3" y1="6" x2="3.01" y2="6" />
                                  <line x1="3" y1="12" x2="3.01" y2="12" />
                                  <line x1="3" y1="18" x2="3.01" y2="18" />
                                </svg>
                                Order Locations
                              </button>
                            </Box>
                          </Box>
                        )} */}
                      </Box>
                    )}

                    {/* Direct location info */}
                    {isDirectType(item.navigation?.type) && (
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: `${buttonColor}10`,
                          borderRadius: 1,
                          border: `1px solid ${buttonColor}30`,
                        }}
                      >
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>Direct Location Selected</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Additional settings for location management are
                          available in the "Location Settings" and "Avatar
                          Settings" tabs that have been added to the navigation.
                        </Typography>
                      </Box>
                    )}

                    {/* Save button for navigation settings */}
                    <Box
                      sx={{
                        mt: 4,
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: buttonColor,
                          color: "white",
                          border: "none",
                          padding: "10px 20px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                        type="button"
                        onClick={handleSave}
                      >
                        Save Navigation Settings
                      </button>
                    </Box>
                  </CardContent>
                </Box>
              </div>
            )}

            {/* Step-by-Step Directions Tab */}
            {activeTab === 1 && stepByStepConfig && (
              <div
                className="menu-card2"
                style={{ padding: "20px", marginTop: "20px" }}
              >
                <Box sx={{ width: "100%", mx: "auto" }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                      Step-by-Step Directions Configuration
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {type === 'category' 
                        ? 'Configure step-by-step voice directions for this category. This configuration will be used by all locations under this category unless they have custom settings.'
                        : 'Configure step-by-step voice directions for this subcategory. You can use the global configuration, inherit from a parent category, or customize it specifically for this subcategory.'}
                    </Typography>

                    <StepByStepDirectionsConfig
                      config={stepByStepConfig}
                      onChange={setStepByStepConfig}
                      showUseParent={type === 'subcategory'} // Show parent selection for subcategory
                      parentOptions={parentOptions || []}
                      buttonColor={buttonColor}
                      disabled={false}
                      userId={userId}
                      entityType={type === 'category' ? 'Category' : 'SubCategory'}
                      entityId={item?._id || null}
                      entityName={getLanguageText(item?.name, currentLanguage) || ''}
                    />

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                      <button
                        style={{
                          backgroundColor: buttonColor,
                          color: "white",
                          border: "none",
                          padding: "10px 20px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                        type="button"
                        onClick={saveStepByStepConfig}
                      >
                        Save Step-by-Step Configuration
                      </button>
                    </Box>
                  </CardContent>
                </Box>
              </div>
            )}
          </Box>
        </div>
      </div>
    </div>
  );
};

 

const getFileType = (fileUrl) => {
  if (!fileUrl) return "blank"; // No URL provided

  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".svg"];
  const videoExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm"];

  if (imageExtensions.some((ext) => fileUrl.toLowerCase().endsWith(ext))) {
    return "image";
  }
  if (videoExtensions.some((ext) => fileUrl.toLowerCase().endsWith(ext))) {
    return "video";
  }
  return "other"; // Other file types or unsupported extensions
};

 

export default WayfinderEditor;
